import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import AppLayout from "@/components/app-layout";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PulseBriefing,
  VoiceBriefingPlayer,
  PulseSamplePreview,
  type PulseBriefing as PulseBriefingType,
  PULSE_BETA_KEY,
} from "@/components/pulse-briefing";
import { useBetaAccessState } from "@/hooks/use-beta-access";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

type PulseStatus = "green" | "yellow" | "red";
type AnalyticsAiSummary = {
  pulseSignal: { status: PulseStatus; headline: string; reason: string };
};

const PULSE_DOT: Record<PulseStatus, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
};
const PULSE_RING: Record<PulseStatus, string> = {
  green: "border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-950/30",
  yellow: "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30",
  red: "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30",
};

// Compact portfolio Pulse signal for the daily pulse page. Only fetches the AI
// summary when the host has the portfolio_analytics beta, so it never fires for
// accounts without the feature (the endpoint 404s for them anyway).
function PortfolioPulseCard() {
  const { granted } = useBetaAccessState("portfolio_analytics");
  const { data } = useQuery<AnalyticsAiSummary>({
    queryKey: ["/api/app/analytics/ai-summary"],
    enabled: granted,
    staleTime: 5 * 60 * 1000,
  });

  if (!granted || !data?.pulseSignal) return null;
  const signal = data.pulseSignal;
  const dot = PULSE_DOT[signal.status] ?? PULSE_DOT.yellow;
  const ring = PULSE_RING[signal.status] ?? PULSE_RING.yellow;

  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 ${ring}`}
      data-testid="card-portfolio-pulse-signal"
    >
      <span className={`mt-1 inline-flex h-3 w-3 shrink-0 rounded-full ${dot}`} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <p className="font-semibold text-gray-900 dark:text-gray-50" data-testid="text-portfolio-pulse-headline">
            {signal.headline}
          </p>
        </div>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="text-portfolio-pulse-reason">
          {signal.reason}
        </p>
      </div>
    </div>
  );
}

export default function AppDailyPulse() {
  // Gate the real Pulse on the same beta flag + tier lock as before. Accounts
  // without access still land on this page — they just get a sample preview
  // with an upgrade CTA instead of the live briefing.
  const { granted: pulseGranted } = useBetaAccessState(PULSE_BETA_KEY);

  const tierLocksQuery = useQuery<{ deniedKeys: string[] }>({
    queryKey: ["/api/app/menu-tier-locks/denied"],
    staleTime: 60_000,
  });
  const pulseTierDenied = (tierLocksQuery.data?.deniedKeys ?? []).includes("pinned.pulse_ai");

  // Fail closed: only grant the live Pulse path once the tier-lock status is
  // actually known and confirms the account is allowed. While the query is
  // loading — or if it errors — we never render the live briefing (which would
  // otherwise fire the real /api/app/pulse/briefing request for a denied tier).
  const hasPulseAccess = pulseGranted && tierLocksQuery.isSuccess && !pulseTierDenied;

  const pulseBriefingQuery = useQuery<PulseBriefingType>({
    queryKey: ["/api/app/pulse/briefing"],
    enabled: hasPulseAccess,
    staleTime: 5 * 60 * 1000,
  });

  // Distinguish "still resolving access" from "confirmed no access" so we can
  // show a brief skeleton instead of flashing the sample preview to users who
  // will turn out to have full access.
  const tierResolving = pulseGranted && tierLocksQuery.isLoading;

  return (
    <AppLayout>
      <div className="p-6 space-y-6" data-testid="page-daily-pulse">
        <div className="flex items-center justify-end gap-2">
          <ExplainerVideoButton pageKey="daily-pulse" compactOnMobile />
          <GuideButton pageKey="daily-pulse" compactOnMobile />
        </div>
        <PortfolioPulseCard />
        {hasPulseAccess ? (
          <>
            <VoiceBriefingPlayer briefingGeneratedAt={pulseBriefingQuery.data?.generatedAt} />
            <PulseBriefing />
          </>
        ) : tierResolving ? (
          <div className="space-y-2" data-testid="loading-daily-pulse">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-3/4" />
          </div>
        ) : (
          <PulseSamplePreview />
        )}
      </div>
      <GuideNudgeBanner pageKey="daily-pulse" />
    </AppLayout>
  );
}
